import { Auth } from "aws-amplify";
import { DEFAULT_APP_VERSION, VARIANT_VX } from "../constants/login";

export async function loadUserVariant(): Promise<string> {
  try {
    if (process.env.REACT_APP_ENVIRONMENT === "OFFLINE") {
      return process.env.REACT_APP_VERSION || DEFAULT_APP_VERSION;
    }
    const user = await Auth.currentAuthenticatedUser();
    // Returned AS-IS (not collapsed to the bare "vx" literal) so a per-user
    // restriction suffix like "vx:v2" survives the round trip — see
    // allowedVariantKeys below, which is what actually reads it.
    return user?.attributes?.["custom:variant"] ?? DEFAULT_APP_VERSION;
  } catch {
    return DEFAULT_APP_VERSION;
  }
}

export function normalizeVariant(v: string) {
  return (v || DEFAULT_APP_VERSION).replace(/^\/+/, "");
}

/**
 * True when custom:variant marks this account as choose-your-system at
 * login ("vx", or "vx:<keys>") rather than pinned to a single version.
 */
export function isMultiVariant(v: string): boolean {
  return v.toLowerCase().startsWith(VARIANT_VX);
}

/**
 * Parses the optional per-user restriction on a "vx" custom:variant value.
 * A bare "vx" returns null — no restriction, VariantSelector offers every
 * configured VARIANT_OPTIONS entry. Suffixing with ":" plus a
 * comma-separated key list — e.g. "vx:v2" — restricts the picker to exactly
 * those keys, so an account meant for only one system can't select the
 * other from the UI.
 *
 * There's no separate Cognito attribute for this: it rides on the same
 * custom:variant value the rest of this file already reads, set by an admin
 * exactly the way "vx" is set today, just with the suffix appended. This is
 * a UI-level convenience, not an authorization boundary — the backend must
 * independently reject any request the authenticated user isn't actually
 * entitled to, the same as it does for org/role scoping elsewhere in the
 * app; nothing here should be treated as the source of truth for access.
 */
export function allowedVariantKeys(v: string): string[] | null {
  const idx = v.indexOf(":");
  if (idx === -1) return null;

  const keys = v
    .slice(idx + 1)
    .split(",")
    .map((key) => key.trim())
    .filter(Boolean);

  return keys.length > 0 ? keys : null;
}
